import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import TableHead from './TableHead';
import VirtualTable from './VirtualTable';

import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import CodeRow from './CodesList/CodeListItems/CodeRow';
import ChildCodeRow from './CodesList/CodeListItems/ChildCodeRow';
import BundleChildStatusRow from './CodesList/CodeListItems/BundleChildStatusRow';
import { actions } from '../../../../../../context';
import BulkActionsBar from '../BulkActionsBar';
import { DaylightContext } from '../../DaylightCodeMapper';
import { compareCodes } from './sortUtils';

const TABLE_HEADER_HEIGHT = 100;

const CodesTable = () => {
    const dispatch = useDispatch();
    const { codeUsageStatsMap } = useContext(DaylightContext);

    const daylightCodes = useSelector(
        (state) => state.daylight.daylightCodes,
        shallowEqual
    );
    const practiceId = useSelector((state) => state.daylight.practice.id);
    const partnerId = useSelector((state) => state.daylight.partnerIdFilter);
    const updatingCodeId = useSelector((state) => state.daylight.updatingCodeId);
    const updatingMappingId = useSelector((state) => state.daylight.updatingMappingId);
    const codeNameFilter = useSelector((state) => state.daylight.codeNameFilter);
    const codeCategoryFilter = useSelector((state) => state.daylight.codeCategoryFilter);
    const codeDescriptionFilter = useSelector((state) => state.daylight.codeDescriptionFilter);
    const codeLocationFilter = useSelector((state) => state.daylight.codeLocationFilter);
    const codeMappingFilter = useSelector((state) => state.daylight.codeMappingFilter);
    const codeHasCommentsFilter = useSelector((state) => state.daylight.codeHasCommentsFilter);
    const codeIsMarkedCompletedFilter = useSelector((state) => state.daylight.codeIsMarkedCompletedFilter);
    const codeNeedsReviewFilter = useSelector((state) => state.daylight.codeNeedsReviewFilter);
    const codeMarkedDisabledFilter = useSelector((state) => state.daylight.codeMarkedDisabledFilter);
    const codeIsBundleFilter = useSelector((state) => state.daylight.codeIsBundleFilter);
    const sortConfig = useSelector((state) => state.daylight.sortConfig, shallowEqual);
    const selectedCodeUsageDetail = useSelector((state) => state.daylight.selectedCodeUsageDetail, shallowEqual);

    const filteredCodes = useMemo(() => {
        return daylightCodes.filter((code) =>
            filterCodeName(code.code, codeNameFilter) &&
            filterCodeCategory(code.codeCategory, codeCategoryFilter) &&
            filterCodeLocation(code.locationId, codeLocationFilter) &&
            filterCodeMappings(code.mappings, codeMappingFilter) &&
            filterCodeDescription(code.codeDescription, codeDescriptionFilter) &&
            filterHasComments(code.mappings, codeHasCommentsFilter) &&
            filterIsMarkedCompleted(code.confirmationStatus, codeIsMarkedCompletedFilter) &&
            filterNeedsReview(code.needsReview, codeNeedsReviewFilter) &&
            filterMarkedDisabled(code.markedDisabled, codeMarkedDisabledFilter) &&
            filterIsBundle(code.isBundleParent, codeIsBundleFilter)
        );
    }, [
        daylightCodes, codeNameFilter, codeCategoryFilter, codeDescriptionFilter,
        codeLocationFilter, codeMappingFilter, codeHasCommentsFilter,
        codeIsMarkedCompletedFilter, codeNeedsReviewFilter, codeMarkedDisabledFilter,
        codeIsBundleFilter
    ]);

    const sortedCodes = useMemo(() => {
        const codes = sortConfig
            ? [...filteredCodes].sort((firstCode, secondCode) =>
                compareCodes(firstCode, secondCode, sortConfig.field, sortConfig.direction, codeUsageStatsMap, selectedCodeUsageDetail)
            )
            : filteredCodes;
        return codes.map((code, index) => ({ ...code, row: index + 1 }));
    }, [filteredCodes, sortConfig, codeUsageStatsMap, selectedCodeUsageDetail]);

    const sortKey = sortConfig ? `${sortConfig.field}-${sortConfig.direction}` : 'default';

    const filteredCodeIds = useMemo(
        () => filteredCodes.map(code => code.id),
        [filteredCodes]
    );

    useEffect(() => {
        dispatch(actions.daylight.setDisplayedCodesCount(sortedCodes.length));
    }, [sortedCodes.length, dispatch]);

    // --- Bundle child-code expansion (lazy, per panel) ---
    const [expandedCodeIds, setExpandedCodeIds] = useState([]);
    const [childCodesByParent, setChildCodesByParent] = useState({}); // codeId -> { status, children }
    const inFlightChildLoads = useRef(new Set());
    // Ref mirror so load decisions read current cache synchronously (setState updaters run later).
    const childCodesRef = useRef(childCodesByParent);
    useEffect(() => {
        childCodesRef.current = childCodesByParent;
    }, [childCodesByParent]);

    // Last parent whose rows changed + a bumping sequence, so the list re-measures from that parent's row.
    const [bundleReset, setBundleReset] = useState({ codeId: null, seq: 0 });
    const bumpBundleReset = useCallback(
        (codeId) => setBundleReset((prev) => ({ codeId, seq: prev.seq + 1 })),
        []
    );

    const loadChildCodes = useCallback(async (codeId, force = false) => {
        if (inFlightChildLoads.current.has(codeId)) return;
        const existing = childCodesRef.current[codeId];
        if (!force && existing?.status === 'loaded') return; // already cached

        inFlightChildLoads.current.add(codeId);
        setChildCodesByParent((prev) => ({
            ...prev,
            [codeId]: { status: 'loading', children: prev[codeId]?.children ?? [] },
        }));
        try {
            const res = await dispatch(
                actions.daylight.getBundleChildCodes(practiceId, codeId, partnerId ?? '')
            );
            if (!res) {
                setChildCodesByParent((prev) => ({ ...prev, [codeId]: { status: 'error', children: [] } }));
                return;
            }
            const payload = res?.data?.data ?? res?.data ?? [];
            const children = Array.isArray(payload) ? payload : [];
            setChildCodesByParent((prev) => ({ ...prev, [codeId]: { status: 'loaded', children } }));
        } catch (error) {
            setChildCodesByParent((prev) => ({ ...prev, [codeId]: { status: 'error', children: [] } }));
        } finally {
            inFlightChildLoads.current.delete(codeId);
            bumpBundleReset(codeId); // re-measure once the result arrives
        }
    }, [dispatch, practiceId, partnerId, bumpBundleReset]);

    const toggleBundle = useCallback((codeId) => {
        setExpandedCodeIds((prev) =>
            prev.includes(codeId)
                ? prev.filter((id) => id !== codeId)
                : [...prev, codeId]
        );
        bumpBundleReset(codeId);
    }, [bumpBundleReset]);

    // Re-fetch already-loaded panels (child rows live outside the grid's optimistic updates).
    const refreshOpenPanels = useCallback(() => {
        expandedCodeIds.forEach((codeId) => {
            if (childCodesRef.current[codeId]?.status === 'loaded') {
                loadChildCodes(codeId, true);
            }
        });
    }, [expandedCodeIds, loadChildCodes]);

    // Lazy-load children when a panel is first expanded.
    useEffect(() => {
        expandedCodeIds.forEach((codeId) => loadChildCodes(codeId));
    }, [expandedCodeIds, loadChildCodes]);

    // Refresh open panels after the grid reloads (top Refresh, partner switch, disable/finalize).
    const skipInitialCodesSync = useRef(true);
    useEffect(() => {
        if (skipInitialCodesSync.current) {
            skipInitialCodesSync.current = false;
            return;
        }
        refreshOpenPanels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [daylightCodes]);

    // Refresh open panels when a row action finishes. Its updating flag clears in the thunk's
    // finally (after the backend commits), so the re-fetch reads the new state without a race.
    // Needed because Mark Complete / Review / Edit / Comments update the grid optimistically only.
    const prevUpdating = useRef({ codeId: '', mappingId: '' });
    useEffect(() => {
        const prev = prevUpdating.current;
        const actionFinished =
            (prev.codeId && !updatingCodeId) || (prev.mappingId && !updatingMappingId);
        prevUpdating.current = { codeId: updatingCodeId, mappingId: updatingMappingId };
        if (actionFinished) refreshOpenPanels();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatingCodeId, updatingMappingId]);

    const codeIndexRowHeights = useMemo(() => {
        return buildRowHeightIndex(sortedCodes, expandedCodeIds, childCodesByParent);
    }, [sortedCodes, expandedCodeIds, childCodesByParent]);

    // First list row of the parent that last changed; the list re-measures from here.
    const bundleResetIndex = useMemo(() => {
        if (bundleReset.codeId == null) return 0;
        const index = codeIndexRowHeights.findIndex(
            (item) => sortedCodes[item.codeIndex]?.id === bundleReset.codeId
        );
        return index === -1 ? 0 : index;
    }, [bundleReset, codeIndexRowHeights, sortedCodes]);

    const getItemSize = (index) => codeIndexRowHeights[index].rowHeight || 40;

    function Row({ index }) {
        const item = codeIndexRowHeights[index];
        const parent = sortedCodes[item.codeIndex];

        if (item.type === 'child') {
            const child = (childCodesByParent[parent.id]?.children ?? [])[item.childIndex];
            if (!child) return null;
            return <ChildCodeRow key={index} code={child} mappingIndex={item.mappingIndex} />;
        }
        if (item.type === 'status') {
            return <BundleChildStatusRow key={index} status={item.status} />;
        }
        return (
            <CodeRow
                key={index}
                code={parent}
                codeIndex={item.codeIndex}
                mappingIndex={item.mappingIndex}
                filteredCodeIds={filteredCodeIds}
                isExpanded={expandedCodeIds.includes(parent.id)}
                onToggleBundle={toggleBundle}
            />
        );
    }

    return (
        <div
            className="codes-table"
            style={{
                ['--table-header-height']: `${TABLE_HEADER_HEIGHT}px`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <div className="table-responsive vt-header" style={{ height: 'var(--table-header-height)', minHeight: 'var(--table-header-height)', flexShrink: 0 }}>
                <table style={{ minHeight: '100px' }} className="fixed-table w-100 bg-light text-dark sticky-top border-bottom text-12">
                    <TableHead filteredCodeIds={filteredCodeIds} />
                </table>
            </div>
            <BulkActionsBar />
            <div
                className="table-responsive vt-body"
                style={{ height: 'calc(100% - var(--table-header-height))', minHeight: 0, flex: 1, overflow: 'hidden' }}
            >
                <VirtualTable
                    width="100%"
                    itemCount={codeIndexRowHeights.length}
                    codeCount={sortedCodes.length}
                    sortKey={sortKey}
                    resetToken={bundleReset.seq}
                    resetIndex={bundleResetIndex}
                    itemSize={getItemSize}
                    row={Row}
                    footer={<tfoot></tfoot>}
                />
            </div>
        </div>
    );
};

// --- Row height indexing for virtualized table ---

const ROW_HEIGHT_WITH_LABEL = 70;
const ROW_HEIGHT_DEFAULT = 40;
const LABEL_INTERVAL = 5;

const shouldDisplayLabel = (index, items) =>
    index === 0 || (index % LABEL_INTERVAL === 0 && index < items.length - 1);

// One list item per mapping row; for an expanded bundle parent, appends its child rows
// (one per child mapping) or a single status row while loading / when empty.
const buildRowHeightIndex = (codes, expandedCodeIds = [], childCodesByParent = {}) => {
    const result = [];

    const pushMappingRows = (mappings, entry) => {
        const rowCount = mappings.length || 1;
        for (let mappingIndex = 0; mappingIndex < rowCount; mappingIndex++) {
            result.push({
                ...entry,
                mappingIndex,
                rowHeight: shouldDisplayLabel(mappingIndex, mappings) ? ROW_HEIGHT_WITH_LABEL : ROW_HEIGHT_DEFAULT,
            });
        }
    };

    codes.forEach((code, codeIndex) => {
        pushMappingRows(code?.mappings ?? [], { type: 'code', codeIndex });

        if (!code?.isBundleParent || !expandedCodeIds.includes(code.id)) return;

        const panel = childCodesByParent[code.id];
        const status = panel?.status ?? 'loading';
        const children = panel?.children ?? [];

        // Keep showing children even while a background refresh is in flight, so an action
        // doesn't briefly collapse the panel to the loading row. Only show a status row when
        // there is nothing to display yet (initial load / empty / error).
        if (children.length > 0) {
            children.forEach((child, childIndex) => {
                pushMappingRows(child?.mappings ?? [], { type: 'child', codeIndex, childIndex });
            });
        } else {
            result.push({
                type: 'status',
                codeIndex,
                status: status === 'loaded' ? 'empty' : status,
                rowHeight: ROW_HEIGHT_DEFAULT,
            });
        }
    });
    return result;
};

// --- Filter predicates (pure functions, single responsibility each) ---

const filterCodeName = (code, filter) => {
    if (!filter) return true;
    return code?.toLowerCase().includes(filter.toLowerCase());
};

const filterCodeCategory = (codeCategory, filter) => {
    if (!filter) return true;
    return codeCategory === filter;
};

const filterCodeLocation = (locationId, filter) => {
    if (!filter) return true;
    return locationId === filter;
};

const filterCodeDescription = (codeDescription, filter) => {
    if (!filter) return true;
    return codeDescription?.toLowerCase().includes(filter.toLowerCase());
};

const filterCodeMappings = (mappings, filter) => {
    if (!filter) return true;
    const normalizedFilter = filter.toLowerCase();
    return mappings?.some((mapping) =>
        mapping.taxonomyCodeNodeLabel?.toLowerCase().includes(normalizedFilter) ||
        mapping.codeName?.toLowerCase().includes(normalizedFilter)
    );
};

const filterHasComments = (mappings, filter) => {
    if (filter === null) return true;
    const hasComments = (m) => m.comments !== null && m.comments?.length > 0;
    return filter ? mappings?.some(hasComments) : mappings?.every((m) => !hasComments(m));
};

const filterIsMarkedCompleted = (confirmationStatus, filter) => {
    if (filter === null) return true;
    return filter ? confirmationStatus === 1 : confirmationStatus === 0;
};

const filterNeedsReview = (needsReview, filter) => {
    if (filter === null) return true;
    return filter ? needsReview : !needsReview;
};

const filterMarkedDisabled = (markedDisabled, filter) => {
    if (filter === null) return true;
    return filter ? markedDisabled : !markedDisabled;
};

const filterIsBundle = (isBundleParent, filter) => {
    if (filter === null) return true;
    return filter ? !!isBundleParent : !isBundleParent;
};

export default CodesTable;
